import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, getDocs, orderBy, deleteDoc, doc, updateDoc, getDocs as getDocsOnce, limit } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { Link } from 'react-router-dom';
import { createSnapshotForTask, testSnapshot, deleteSnapshot as deleteSnapshotCallable } from '../../lib/callables';

interface Submission {
  id: string;
  taskId: string;
  taskTitle: string;
  taskType: string;
  category: string;
  subcategory?: string;
  itemType?: string;
  imageUrl?: string;
  mediaUrl?: string;
  labels: any;
  status: string;
  createdAt: any;
  completedAt?: any;
  approvedAt?: any;
  labeledAt?: any;
  labeledBy?: string;
  approvedBy?: string;
  classificationSnapshotStatus?: 'ready_for_training' | 'in_snapshot' | 'in_model';
  snapshotId?: string;
  snapshotDate?: any;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  category: string;
  subcategory?: string;
  itemType?: string;
  taskType: string;
  active?: boolean;
}

interface DatasetSnapshot {
  id: string;
  taskId: string;
  taskType: 'classification' | 'detection';
  name?: string;
  uri: string;
  counts: {
    train: number;
    val: number;
    test: number;
    positives: number;
    negatives: number;
  };
  classes: string[];
  split: { train: number; val: number; test: number };
  sourceQuery: {
    positives: { taskId: string };
    negatives?: { category: string };
  };
  createdAt: any;
  createdBy: string;
}

export default function Datasets() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [snapshots, setSnapshots] = useState<DatasetSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    task: 'all' as string,
    category: 'all' as string,
    subcategory: 'all' as string,
    itemType: 'all' as string,
    taskType: 'all' as string,
    dateRange: 'all' as string,
    classificationSnapshotStatus: 'all' as string
  });
  const [activeTab, setActiveTab] = useState<'overview' | 'submissions' | 'snapshots'>('overview');
  const [snapshotFilters, setSnapshotFilters] = useState({ task: 'all', hasZeroVal: false, taskType: 'all' });

  // Selection state for submissions
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<Set<string>>(new Set());
  
  // Snapshot creation state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateFromSelectionModal, setShowCreateFromSelectionModal] = useState(false);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotConfig, setSnapshotConfig] = useState({
    split: { train: 0.8, val: 0.1, test: 0.1 },
    maxPerClass: undefined as number | undefined,
    minResolution: { w: 256, h: 256 }
  });

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [sendingBackToQueue, setSendingBackToQueue] = useState(false);
  const [isEditingSubcategory, setIsEditingSubcategory] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<string>('');
  const [savingSubcategory, setSavingSubcategory] = useState(false);
  const [isEditingItemType, setIsEditingItemType] = useState(false);
  const [editingItemType, setEditingItemType] = useState<string>('');
  const [savingItemType, setSavingItemType] = useState(false);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [modalLabels, setModalLabels] = useState<{
    leftPct: number;
    topPct: number;
    widthPct: number;
    heightPct: number;
    classId: string;
  }[]>([]);

  // Load tasks for reference
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'tasks'), (snapshot) => {
      const tasksData: Record<string, Task> = {};
      snapshot.docs.forEach(doc => {
        tasksData[doc.id] = { id: doc.id, ...doc.data() } as Task;
      });
      setTasks(tasksData);
      console.log('Tasks loaded:', Object.keys(tasksData).length, 'tasks');
    }, (error) => {
      console.error('Error loading tasks:', error);
    });
    return unsubscribe;
  }, []);

  // Load categories and subcategories for editing
  useEffect(() => {
    const unsubscribeCategories = onSnapshot(collection(db, 'categories'), (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
      setCategories(categoriesData);
    }, (error) => {
      console.error('Error loading categories:', error);
    });

    const unsubscribeSubcategories = onSnapshot(collection(db, 'subcategories'), (snapshot) => {
      const subcategoriesData = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
      setSubcategories(subcategoriesData);
    }, (error) => {
      console.error('Error loading subcategories:', error);
    });

    return () => {
      unsubscribeCategories();
      unsubscribeSubcategories();
    };
  }, []);

  // Load completed submissions (fully approved)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'submissions'), where('status', 'in', ['approved', 'completed'])),
      (snapshot) => {
        const submissionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Submission[];
        
        // Sort by completion date (newest first)
        submissionsData.sort((a, b) => {
          const aTime = a.completedAt?.toMillis?.() || a.completedAt || 0;
          const bTime = b.completedAt?.toMillis?.() || b.completedAt || 0;
          return bTime - aTime;
        });
        
        setSubmissions(submissionsData);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  // Load dataset snapshots
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'dataset_snapshots'), where('taskType', '==', 'classification'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const snapshotsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as DatasetSnapshot[];
        setSnapshots(snapshotsData);
      }
    );
    return unsubscribe;
  }, []);

  // Reset editing states when changing the selected submission
  useEffect(() => {
    setIsEditingSubcategory(false);
    setEditingSubcategory('');
    setIsEditingItemType(false);
    setEditingItemType('');
  }, [selectedSubmission]);

  const getSubmissionItemType = (submission: Submission) => {
    const task = tasks[submission.taskId];
    return submission.itemType || (submission as any)?.taskItemType || task?.itemType || '';
  };

  // Filter submissions based on search and filters
  const filteredSubmissions = submissions.filter(submission => {
    if (submission.taskType === 'Video') return false;
    const task = tasks[submission.taskId];
    
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        submission.id.toLowerCase().includes(searchLower) ||
        submission.taskTitle?.toLowerCase().includes(searchLower) ||
        submission.category?.toLowerCase().includes(searchLower) ||
        task?.title?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Task filter
    if (filters.task !== 'all' && submission.taskId !== filters.task) {
      return false;
    }
    
    // Category filter
    if (filters.category !== 'all' && submission.category !== filters.category) {
      return false;
    }
    
    // Subcategory filter
    if (filters.subcategory !== 'all' && submission.subcategory !== filters.subcategory) {
      return false;
    }
    
    // Item type filter
    const submissionItemType = getSubmissionItemType(submission);
    if (filters.itemType !== 'all') {
      if (filters.itemType === 'None') {
        // Filter for submissions with no itemType (empty string, null, or undefined)
        if (submissionItemType && submissionItemType.trim()) {
          return false;
        }
      } else {
        // Filter for specific itemType
        if (submissionItemType !== filters.itemType) {
          return false;
        }
      }
    }
    
    // Task type filter
    if (filters.taskType !== 'all' && submission.taskType !== filters.taskType) {
      return false;
    }
    
    // Snapshot status filter
    if (filters.classificationSnapshotStatus !== 'all') {
      const status = (submission as any).classificationSnapshotStatus || 'ready_for_training';
      if (status !== filters.classificationSnapshotStatus) return false;
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const now = Date.now();
      const completedTime = submission.completedAt?.toMillis?.() || submission.completedAt || 0;
      const daysDiff = (now - completedTime) / (1000 * 60 * 60 * 24);
      
      if (filters.dateRange === 'today' && daysDiff > 1) return false;
      if (filters.dateRange === 'week' && daysDiff > 7) return false;
      if (filters.dateRange === 'month' && daysDiff > 30) return false;
    }
    
    return true;
  });

  // Get unique values for filter dropdowns
  const uniqueCategories = [...new Set(submissions.map(s => s.category).filter(Boolean))];
  // Filter subcategories based on selected category (like user/client portals)
  const uniqueSubcategories = filters.category !== 'all'
    ? [...new Set(
        submissions
          .filter(s => s.category === filters.category && s.subcategory)
          .map(s => s.subcategory)
          .filter(Boolean)
      )]
    : [...new Set(submissions.map(s => s.subcategory).filter(Boolean))];
  const uniqueItemTypes = (() => {
    const filteredSubmissions = submissions
      .filter(s => filters.category === 'all' || s.category === filters.category)
      .filter(s => filters.subcategory === 'all' || s.subcategory === filters.subcategory);
    
    const itemTypes = new Set<string>();
    let hasEmptyItemType = false;
    
    filteredSubmissions.forEach(s => {
      const itemType = getSubmissionItemType(s);
      if (itemType && itemType.trim()) {
        itemTypes.add(itemType);
      } else {
        hasEmptyItemType = true;
      }
    });
    
    const result = Array.from(itemTypes).sort((a, b) => a.localeCompare(b));
    if (hasEmptyItemType) {
      result.unshift('None');
    }
    return result;
  })();
  const uniqueTaskTypes = [...new Set(submissions.map(s => s.taskType).filter(Boolean))];
  
  // Get tasks, prioritizing active ones
  const allTasks = Object.values(tasks);
  // Cascade: only show tasks that have submissions matching current category/subcategory/itemType filters
  const tasksWithSubmissions = new Set(
    submissions
      .filter(s => filters.category === 'all' || s.category === filters.category)
      .filter(s => filters.subcategory === 'all' || s.subcategory === filters.subcategory)
      .filter(s => {
        if (filters.itemType === 'all') return true;
        const itemType = getSubmissionItemType(s);
        if (filters.itemType === 'None') return !itemType || !itemType.trim();
        return itemType === filters.itemType;
      })
      .filter(s => {
        if (filters.classificationSnapshotStatus === 'all') return true;
        const status = (s as any).classificationSnapshotStatus || 'ready_for_training';
        return status === filters.classificationSnapshotStatus;
      })
      .map(s => s.taskId)
  );
  const activeTasks = allTasks.filter(task => task.active === true && tasksWithSubmissions.has(task.id));
  const inactiveTasks = allTasks.filter(task => task.active === false && tasksWithSubmissions.has(task.id));
  const uniqueTasks = [...activeTasks, ...inactiveTasks];

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getLabelsCount = (labels: any) => {
    if (!labels) return 0;
    if (Array.isArray(labels)) return labels.length;
    if (typeof labels === 'object') return Object.keys(labels).length;
    return 0;
  };

  const exportDataset = async (format: 'json' | 'csv') => {
    try {
      const data = filteredSubmissions.map(submission => {
        const task = tasks[submission.taskId];
        return {
          id: submission.id,
          taskId: submission.taskId,
          taskTitle: task?.title || submission.taskTitle,
          category: submission.category,
          taskType: submission.taskType,
          imageUrl: submission.imageUrl || submission.mediaUrl,
          labels: submission.labels,
          labelsCount: getLabelsCount(submission.labels),
          completedAt: formatDate(submission.completedAt),
          labeledBy: submission.labeledBy,
          approvedBy: submission.approvedBy
        };
      });

      if (format === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dataset_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === 'csv') {
        const headers = ['ID', 'Task ID', 'Task Title', 'Category', 'Task Type', 'Image URL', 'Labels Count', 'Completed At', 'Labeled By', 'Approved By'];
        const csvContent = [
          headers.join(','),
          ...data.map(row => [
            row.id,
            row.taskId,
            `"${row.taskTitle}"`,
            row.category,
            row.taskType,
            `"${row.imageUrl}"`,
            row.labelsCount,
            `"${row.completedAt}"`,
            row.labeledBy || '',
            row.approvedBy || ''
          ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dataset_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Toggle selection for a submission
  const toggleSubmissionSelection = (submissionId: string) => {
    setSelectedSubmissionIds(prev => {
      const next = new Set(prev);
      if (next.has(submissionId)) {
        next.delete(submissionId);
      } else {
        next.add(submissionId);
      }
      return next;
    });
  };

  // Select all filtered submissions
  const selectAllFiltered = () => {
    const allIds = new Set(filteredSubmissions.map(s => s.id));
    setSelectedSubmissionIds(allIds);
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedSubmissionIds(new Set());
  };

  // Create snapshot for selected task (from Snapshots tab)
  const createSnapshot = async () => {
    if (filters.task === 'all') {
      alert('Please select a specific task to create a training snapshot.');
      return;
    }

    try {
      setCreatingSnapshot(true);
      const result = await createSnapshotForTask({
        taskId: filters.task,
        name: snapshotName.trim(),
        ...snapshotConfig
      });
      
      alert(`Snapshot created successfully!\nID: ${result.snapshotId}\nURI: ${result.uri}\nCounts: ${JSON.stringify(result.counts)}`);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Create snapshot error:', error);
      alert('Failed to create snapshot. Please try again.');
    } finally {
      setCreatingSnapshot(false);
    }
  };

  // Create snapshot from selected submissions
  const createSnapshotFromSelection = async () => {
    if (selectedSubmissionIds.size === 0) {
      alert('Please select at least one submission.');
      return;
    }

    // Get selected submissions
    const selectedSubmissions = filteredSubmissions.filter(s => selectedSubmissionIds.has(s.id));
    
    // Check if all selected submissions are from the same task
    const taskIds = new Set(selectedSubmissions.map(s => s.taskId));
    if (taskIds.size > 1) {
      alert('All selected submissions must be from the same task. Please select submissions from only one task.');
      return;
    }

    if (taskIds.size === 0) {
      alert('No valid task found for selected submissions.');
      return;
    }

    const taskId = Array.from(taskIds)[0];
    const task = tasks[taskId];

    if (!snapshotName.trim()) {
      alert('Please enter a name for the snapshot.');
      return;
    }

    try {
      setCreatingSnapshot(true);
      const result = await createSnapshotForTask({
        taskId: taskId,
        name: snapshotName.trim(),
        ...snapshotConfig
      });
      
      // Note: The snapshot will be created with the auto-generated ID from the callable
      // The name field is not part of the DatasetSnapshot interface, but we can store it
      // For now, we'll just show success and let the user know the snapshot was created
      alert(`Snapshot "${snapshotName}" created successfully!\nID: ${result.snapshotId}\nURI: ${result.uri}\nCounts: ${JSON.stringify(result.counts)}`);
      
      // Clear selection and close modal
      setSelectedSubmissionIds(new Set());
      setSnapshotName('');
      setShowCreateFromSelectionModal(false);
    } catch (error) {
      console.error('Create snapshot error:', error);
      alert('Failed to create snapshot. Please try again.');
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const deleteSubmission = async (submissionId: string, submissionTitle: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete this submission?\n\nSubmission: ${submissionTitle}\nID: ${submissionId}\n\n⚠️ WARNING: This will permanently delete the submission and all its data. It will NOT be sent back to any queue.`
    );
    if (!confirmed) return;
    
    try {
      await deleteDoc(doc(db, 'submissions', submissionId));
      console.log('✅ Submission permanently deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting submission:', error);
      alert(`Failed to delete submission: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const deleteSnapshot = async (snapshotId: string, taskTitle: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete this dataset snapshot?\n\nTask: ${taskTitle}\nSnapshot ID: ${snapshotId}\n\nThis action cannot be undone and will reset all included submissions to "Ready for Training".`
    );
    if (!confirmed) return;

    try {
      await deleteSnapshotCallable({ snapshotId });
      console.log('✅ Snapshot deleted and submissions reset successfully');
    } catch (error) {
      console.error('❌ Error deleting snapshot:', error);
      alert(`Failed to delete snapshot: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const sendBackToQueue = async (submission: Submission) => {
    const confirmed = window.confirm(
      `Are you sure you want to send this submission back to the labeling queue?\n\nSubmission: ${submission.taskTitle || tasks[submission.taskId]?.title || 'Untitled'}\nID: ${submission.id}\n\nThis will change the status from 'completed' to 'pending' and make it available for re-labeling.`
    );
    if (!confirmed) return;

    try {
      setSendingBackToQueue(true);
      await updateDoc(doc(db, 'submissions', submission.id), {
        status: 'pending',
        sentBackAt: new Date(),
        sentBackBy: 'admin',
        previousStatus: 'completed',
        queueType: 'labeling' // Explicitly set to labeling queue
      });
      console.log('✅ Submission sent back to labeling queue successfully');
      setShowDetailsModal(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('❌ Error sending submission back to queue:', error);
      alert(`Failed to send submission back to queue: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingBackToQueue(false);
    }
  };

  const handleEditSubcategory = () => {
    if (selectedSubmission) {
      const task = tasks[selectedSubmission.taskId];
      // Use task's subcategory if available, otherwise fall back to submission's
      const currentSubcategory = task?.subcategory || selectedSubmission.subcategory || '';
      setEditingSubcategory(currentSubcategory);
      setIsEditingSubcategory(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSubcategory(false);
    setEditingSubcategory('');
  };

  const handleSaveSubcategory = async () => {
    if (!selectedSubmission) return;

    try {
      setSavingSubcategory(true);
      const taskId = selectedSubmission.taskId;
      const task = tasks[taskId];
      
      if (!task) {
        alert('Task not found');
        return;
      }

      // Get the subcategory name from the subcategory object
      const subcategoryObj = subcategories.find(sub => sub.id === editingSubcategory || sub.name === editingSubcategory);
      const subcategoryName = subcategoryObj?.name || editingSubcategory;

      // Update the task's subcategory
      await updateDoc(doc(db, 'tasks', taskId), {
        subcategory: subcategoryName,
        updatedAt: new Date().toISOString()
      });

      // Update the submission's subcategory as well
      await updateDoc(doc(db, 'submissions', selectedSubmission.id), {
        subcategory: subcategoryName
      });

      // Update local state
      setSelectedSubmission({
        ...selectedSubmission,
        subcategory: subcategoryName
      });

      // Update tasks state
      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          subcategory: subcategoryName
        }
      }));

      setIsEditingSubcategory(false);
      alert('Subcategory updated successfully!');
    } catch (error) {
      console.error('Error updating subcategory:', error);
      alert(`Failed to update subcategory: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSavingSubcategory(false);
    }
  };

  // Get available subcategories for the task's category
  const getAvailableSubcategories = () => {
    if (!selectedSubmission) return [];
    const task = tasks[selectedSubmission.taskId];
    if (!task || !task.category) return [];

    // Find the category by name
    const categoryName = task.category;
    const category = categories.find(cat => cat.name === categoryName);
    
    if (!category) {
      // Fallback: if category not found, return all subcategories
      return subcategories;
    }

    // Filter subcategories by categoryId
    return subcategories.filter(sub => {
      return sub.categoryId === category.id;
    });
  };

  const handleEditItemType = () => {
    if (selectedSubmission) {
      const currentItemType = getSubmissionItemType(selectedSubmission);
      setEditingItemType(currentItemType);
      setIsEditingItemType(true);
    }
  };

  const handleCancelItemTypeEdit = () => {
    setIsEditingItemType(false);
    setEditingItemType('');
  };

  const handleSaveItemType = async () => {
    if (!selectedSubmission) return;

    try {
      setSavingItemType(true);
      const taskId = selectedSubmission.taskId;
      const task = tasks[taskId];
      
      if (!task) {
        alert('Task not found');
        return;
      }

      const trimmedItemType = editingItemType.trim();

      await updateDoc(doc(db, 'tasks', taskId), {
        itemType: trimmedItemType,
        updatedAt: new Date().toISOString()
      });

      await updateDoc(doc(db, 'submissions', selectedSubmission.id), {
        itemType: trimmedItemType
      });

      setSelectedSubmission({
        ...selectedSubmission,
        itemType: trimmedItemType
      });

      setTasks(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          itemType: trimmedItemType
        }
      }));

      setIsEditingItemType(false);
      alert('Item type updated successfully!');
    } catch (error) {
      console.error('Error updating item type:', error);
      alert(`Failed to update item type: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSavingItemType(false);
    }
  };

  const getAvailableItemTypes = () => {
    if (!selectedSubmission) return [];
    const task = tasks[selectedSubmission.taskId];
    const categoryName = task?.category || selectedSubmission.category;
    
    const itemTypeSet = new Set<string>();
    
    Object.values(tasks).forEach(taskObj => {
      if (!taskObj?.itemType) return;
      if (!categoryName || taskObj.category === categoryName) {
        itemTypeSet.add(taskObj.itemType);
      }
    });
    
    submissions.forEach(sub => {
      if (categoryName && sub.category !== categoryName) return;
      const type = getSubmissionItemType(sub);
      if (type) {
        itemTypeSet.add(type);
      }
    });
    
    return Array.from(itemTypeSet).sort((a, b) => a.localeCompare(b));
  };

  // When opening details modal, try to derive accurate labels from latest labeling job
  useEffect(() => {
    const loadDerivedLabels = async () => {
      if (!showDetailsModal || !selectedSubmission) {
        setModalLabels([]);
        return;
      }

      try {
        // First try: use labels on submission if they look valid
        const rawLabels: any = (selectedSubmission as any).labels;
        let boxes: { x1: number; y1: number; x2: number; y2: number; classId: string }[] = [];

        const naturalWidth = imageRef.current?.naturalWidth || 0;
        const naturalHeight = imageRef.current?.naturalHeight || 0;

        const toPct = (x: number, total: number) => (total > 0 ? (x / total) * 100 : 0);

        const setFromBoxes = () => {
          setModalLabels(
            boxes.map(b => ({
              leftPct: toPct(b.x1, naturalWidth),
              topPct: toPct(b.y1, naturalHeight),
              widthPct: toPct(Math.max(0, b.x2 - b.x1), naturalWidth),
              heightPct: toPct(Math.max(0, b.y2 - b.y1), naturalHeight),
              classId: b.classId || 'unknown'
            }))
          );
        };

        // Case A: submission.labels is array of objects
        if (Array.isArray(rawLabels) && rawLabels.length > 0) {
          boxes = rawLabels.map((l: any) => {
            if (Array.isArray(l.bbox) && l.bbox.length === 4) {
              const [x1, y1, x2, y2] = l.bbox;
              return { x1, y1, x2, y2, classId: l.categoryId || l.classId || l.class || 'unknown' };
            }
            // Support x,y,width,height (pixels)
            if (typeof l.x === 'number' && typeof l.y === 'number' && typeof l.width === 'number' && typeof l.height === 'number') {
              return { x1: l.x, y1: l.y, x2: l.x + l.width, y2: l.y + l.height, classId: l.categoryId || l.classId || l.class || 'unknown' };
            }
            // Support percent values 0..1
            if (typeof l.x === 'number' && l.x > 0 && l.x <= 1 && typeof l.y === 'number' && typeof l.width === 'number' && typeof l.height === 'number') {
              return { x1: l.x * naturalWidth, y1: l.y * naturalHeight, x2: (l.x + l.width) * naturalWidth, y2: (l.y + l.height) * naturalHeight, classId: l.categoryId || l.classId || l.class || 'unknown' };
            }
            return { x1: 0, y1: 0, x2: 0, y2: 0, classId: 'unknown' };
          });
          setFromBoxes();
          return;
        }

        // Case B: fetch latest labeling job for this submission and derive from labelsDraft/final
        const jobsQ = query(
          collection(db, 'labelingJobs'),
          where('submissionId', '==', selectedSubmission.id),
          orderBy('updatedAt', 'desc'),
          limit(1)
        );
        const jobsSnap = await getDocsOnce(jobsQ);
        if (!jobsSnap.empty) {
          const jobDoc = jobsSnap.docs[0];
          const jobData: any = jobDoc.data();
          const src = (jobData.finalLabels && jobData.finalLabels.length > 0)
            ? jobData.finalLabels
            : jobData.labelsDraft || [];
          boxes = src.map((l: any) => {
            if (Array.isArray(l.bbox) && l.bbox.length === 4) {
              const [x1, y1, x2, y2] = l.bbox;
              return { x1, y1, x2, y2, classId: l.categoryId || 'unknown' };
            }
            return { x1: 0, y1: 0, x2: 0, y2: 0, classId: 'unknown' };
          });
          setFromBoxes();
          return;
        }

        // Fallback: no labels
        setModalLabels([]);
      } catch (err) {
        console.error('Failed to load derived labels for modal:', err);
        setModalLabels([]);
      }
    };

    // Defer slightly to allow imageRef to populate natural sizes
    const t = setTimeout(loadDerivedLabels, 50);
    return () => clearTimeout(t);
  }, [showDetailsModal, selectedSubmission]);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Datasets</h1>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading training snapshots...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Classification Snapshots</h1>
          <p className="text-gray-600 mt-1">
            Approved submissions for classification model training and auto-approval scoring.
          </p>
        </div>
        <div className="flex space-x-2">
          {activeTab === 'submissions' && (
            <>
              <button
                onClick={() => {
                  if (selectedSubmissionIds.size === 0) {
                    alert('Please select at least one submission to create a snapshot.');
                    return;
                  }
                  const selectedSubmissions = filteredSubmissions.filter(s => selectedSubmissionIds.has(s.id));
                  const taskIds = new Set(selectedSubmissions.map(s => s.taskId));
                  if (taskIds.size > 1) {
                    alert('All selected submissions must be from the same task. Please select submissions from only one task.');
                    return;
                  }
                  setShowCreateFromSelectionModal(true);
                }}
                disabled={selectedSubmissionIds.size === 0}
                className="px-4 py-2 bg-[#00e3ff] text-black rounded-lg hover:bg-[#00cce6] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Snapshot ({selectedSubmissionIds.size})
              </button>
              {selectedSubmissionIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Clear Selection
                </button>
              )}
              <button
                onClick={() => exportDataset('json')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Export JSON
              </button>
              <button
                onClick={() => exportDataset('csv')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Export CSV
              </button>
            </>
          )}
          {activeTab === 'snapshots' && (
            <div className="flex flex-col items-end">
              <button
                onClick={() => setShowCreateModal(true)}
                disabled={filters.task === 'all'}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#00e3ff' }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#00cce6')}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00e3ff'}
                title={filters.task === 'all' ? 'Please select a specific task first' : 'Create a dataset snapshot'}
              >
                Create Snapshot
              </button>
              {filters.task === 'all' && (
                <p className="text-xs text-gray-500 mt-1">Select a task first</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('submissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'submissions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Submissions
          </button>
          <button
            onClick={() => setActiveTab('snapshots')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'snapshots'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Snapshots
          </button>
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Status stat cards - clickable */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className="bg-white p-4 rounded-lg border cursor-pointer hover:border-green-400"
              onClick={() => {
                setFilters(prev => ({ ...prev, classificationSnapshotStatus: 'ready_for_training' }));
                setActiveTab('submissions');
              }}
            >
              <div className="text-2xl font-bold text-green-600">
                {submissions.filter(s => s.taskType !== 'Video' && (!s.classificationSnapshotStatus || s.classificationSnapshotStatus === 'ready_for_training')).length}
              </div>
              <div className="text-sm text-gray-600">Ready for Training</div>
              <div className="text-xs text-blue-500 mt-1">Click to view →</div>
            </div>
            <div
              className="bg-white p-4 rounded-lg border cursor-pointer hover:border-yellow-400"
              onClick={() => {
                setFilters(prev => ({ ...prev, classificationSnapshotStatus: 'in_snapshot' }));
                setActiveTab('submissions');
              }}
            >
              <div className="text-2xl font-bold text-yellow-600">
                {submissions.filter(s => s.taskType !== 'Video' && s.classificationSnapshotStatus === 'in_snapshot').length}
              </div>
              <div className="text-sm text-gray-600">In Snapshot</div>
              <div className="text-xs text-blue-500 mt-1">Click to view →</div>
            </div>
            <div
              className="bg-white p-4 rounded-lg border cursor-pointer hover:border-purple-400"
              onClick={() => {
                setFilters(prev => ({ ...prev, classificationSnapshotStatus: 'in_model' }));
                setActiveTab('submissions');
              }}
            >
              <div className="text-2xl font-bold text-purple-600">
                {submissions.filter(s => s.taskType !== 'Video' && s.classificationSnapshotStatus === 'in_model').length}
              </div>
              <div className="text-sm text-gray-600">In Model</div>
              <div className="text-xs text-blue-500 mt-1">Click to view →</div>
            </div>
          </div>

          {/* Task list sorted by Ready for Training count */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Tasks Ready for Snapshot</h3>
              <p className="text-sm text-gray-500">Sorted by number of submissions ready for training. Click a task to view its submissions.</p>
            </div>
            <div className="divide-y divide-gray-100">
              {(() => {
                // Count ready_for_training submissions per task
                const taskCounts: Record<string, number> = {};
                submissions.forEach(s => {
                  if ((!s.classificationSnapshotStatus || s.classificationSnapshotStatus === 'ready_for_training') && s.taskType !== 'Video' && tasks[s.taskId]?.taskType !== 'Video') {
                    taskCounts[s.taskId] = (taskCounts[s.taskId] || 0) + 1;
                  }
                });

                // Sort tasks by count descending, filter out tasks with 0
                const sortedTasks = Object.entries(taskCounts)
                  .filter(([_, count]) => count > 0)
                  .sort(([, a], [, b]) => b - a);

                if (sortedTasks.length === 0) {
                  return (
                    <div className="px-6 py-8 text-center text-gray-500">
                      No tasks with submissions ready for training.
                    </div>
                  );
                }

                return sortedTasks.map(([taskId, count]) => {
                  const task = tasks[taskId];
                  return (
                    <div
                      key={taskId}
                      className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setFilters(prev => ({
                          ...prev,
                          task: taskId,
                          classificationSnapshotStatus: 'ready_for_training'
                        }));
                        setActiveTab('submissions');
                      }}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {task?.title || taskId}
                        </div>
                        <div className="text-xs text-gray-500">
                          {task?.category || ''}{task?.subcategory ? ` · ${task.subcategory}` : ''}{task?.itemType ? ` · ${task.itemType}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          count >= 50 ? 'bg-green-100 text-green-800' :
                          count >= 20 ? 'bg-blue-100 text-blue-800' :
                          count >= 5 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {count} ready
                        </span>
                        <span className="text-gray-400 text-xs">→</span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'submissions' && (
        <>
          {/* Snapshot Status Stats - always shows totals from ALL submissions regardless of filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              className={`bg-white p-4 rounded-lg border cursor-pointer hover:border-green-400 ${filters.classificationSnapshotStatus === 'ready_for_training' ? 'border-green-500 ring-2 ring-green-200' : ''}`}
              onClick={() => setFilters(prev => ({ ...prev, classificationSnapshotStatus: prev.classificationSnapshotStatus === 'ready_for_training' ? 'all' : 'ready_for_training' }))}
            >
              <div className="text-2xl font-bold text-green-600">
                {submissions.filter(s => s.taskType !== 'Video' && (!s.classificationSnapshotStatus || s.classificationSnapshotStatus === 'ready_for_training')).length}
              </div>
              <div className="text-sm text-gray-600">Ready for Training</div>
            </div>
            <div
              className={`bg-white p-4 rounded-lg border cursor-pointer hover:border-yellow-400 ${filters.classificationSnapshotStatus === 'in_snapshot' ? 'border-yellow-500 ring-2 ring-yellow-200' : ''}`}
              onClick={() => setFilters(prev => ({ ...prev, classificationSnapshotStatus: prev.classificationSnapshotStatus === 'in_snapshot' ? 'all' : 'in_snapshot' }))}
            >
              <div className="text-2xl font-bold text-yellow-600">
                {submissions.filter(s => s.taskType !== 'Video' && s.classificationSnapshotStatus === 'in_snapshot').length}
              </div>
              <div className="text-sm text-gray-600">In Snapshot</div>
            </div>
            <div
              className={`bg-white p-4 rounded-lg border cursor-pointer hover:border-purple-400 ${filters.classificationSnapshotStatus === 'in_model' ? 'border-purple-500 ring-2 ring-purple-200' : ''}`}
              onClick={() => setFilters(prev => ({ ...prev, classificationSnapshotStatus: prev.classificationSnapshotStatus === 'in_model' ? 'all' : 'in_model' }))}
            >
              <div className="text-2xl font-bold text-purple-600">
                {submissions.filter(s => s.taskType !== 'Video' && s.classificationSnapshotStatus === 'in_model').length}
              </div>
              <div className="text-sm text-gray-600">In Model</div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600">{submissions.length}</div>
              <div className="text-sm text-gray-600">Total Approved</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">{filteredSubmissions.length}</div>
              <div className="text-sm text-gray-600">Filtered Results</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600">
                {submissions.reduce((sum, s) => sum + getLabelsCount(s.labels), 0)}
              </div>
              <div className="text-sm text-gray-600">Total Labels</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600">{uniqueCategories.length}</div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
          </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-9 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.classificationSnapshotStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, classificationSnapshotStatus: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="ready_for_training">Ready for Training</option>
              <option value="in_snapshot">In Snapshot</option>
              <option value="in_model">In Model</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID, title, category..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, category: e.target.value, subcategory: 'all', itemType: 'all' }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
            <select
              value={filters.subcategory}
              onChange={(e) => setFilters(prev => ({ ...prev, subcategory: e.target.value, itemType: 'all' }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={filters.category === 'all'}
            >
              <option value="all">All Subcategories</option>
              {uniqueSubcategories.map(subcategory => (
                <option key={subcategory} value={subcategory}>{subcategory}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
            <select
              value={filters.itemType}
              onChange={(e) => setFilters(prev => ({ ...prev, itemType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={filters.category === 'all' && filters.subcategory === 'all'}
            >
              <option value="all">All Item Types</option>
              {uniqueItemTypes.map(itemType => (
                <option key={itemType} value={itemType}>{itemType}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
            <select
              value={filters.taskType}
              onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {uniqueTaskTypes.map(taskType => (
                <option key={taskType} value={taskType}>{taskType}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
            <select
              value={filters.task}
              onChange={(e) => setFilters(prev => ({ ...prev, task: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Tasks</option>
              {loading ? (
                <option value="" disabled>Loading tasks...</option>
              ) : uniqueTasks.length === 0 ? (
                <option value="" disabled>No tasks found - Check your database</option>
              ) : (
                uniqueTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.active ? '🟢 ' : '🔴 '}{task.title}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({ task: 'all', category: 'all', subcategory: 'all', itemType: 'all', taskType: 'all', dateRange: 'all', classificationSnapshotStatus: 'all' });
              }}
              className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredSubmissions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">No approved datasets found</div>
          <p className="text-gray-400 mb-4">
            {searchTerm || filters.task !== 'all' || filters.category !== 'all' || filters.subcategory !== 'all' || filters.itemType !== 'all' || filters.taskType !== 'all' || filters.dateRange !== 'all'
              ? 'Try adjusting your filters or search terms'
              : 'Complete some labeling tasks to see approved datasets here'
            }
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {/* Select All Header */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filteredSubmissions.length > 0 && filteredSubmissions.every(s => selectedSubmissionIds.has(s.id))}
                onChange={(e) => {
                  if (e.target.checked) {
                    selectAllFiltered();
                  } else {
                    clearSelection();
                  }
                }}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Select All ({filteredSubmissions.length} on this page)
              </span>
            </label>
          </div>
          <ul className="divide-y divide-gray-200">
            {filteredSubmissions.map((submission) => {
              const task = tasks[submission.taskId];
              const labelsCount = getLabelsCount(submission.labels);
              
              return (
                <li key={submission.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={selectedSubmissionIds.has(submission.id)}
                          onChange={() => toggleSubmissionSelection(submission.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex-shrink-0">
                        <img 
                          src={submission.imageUrl || submission.mediaUrl} 
                          alt="Dataset item" 
                          className="h-16 w-16 object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-gray-900">
                            {submission.taskTitle || task?.title || 'Untitled'}
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Approved
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            {labelsCount} labels
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Task: {task?.title || submission.taskId} • Category: {submission.category} {submission.subcategory && `• Subcategory: ${submission.subcategory}`} • Type: {submission.taskType}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {submission.id.slice(-8)} • Completed: {formatDate(submission.completedAt)}
                        </div>
                        {submission.labeledBy && (
                          <div className="text-sm text-gray-500">
                            Labeled by: {submission.labeledBy}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        submission.classificationSnapshotStatus === 'in_model'
                          ? 'bg-purple-100 text-purple-800'
                          : submission.classificationSnapshotStatus === 'in_snapshot'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {submission.classificationSnapshotStatus === 'in_model'
                          ? 'In Model'
                          : submission.classificationSnapshotStatus === 'in_snapshot'
                          ? 'In Snapshot'
                          : 'Ready for Training'}
                      </span>
                      
                      {/* View Details Button */}
                      <button
                        onClick={() => {
                          setSelectedSubmission(submission);
                          setShowDetailsModal(true);
                        }}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        View Details
                      </button>
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => deleteSubmission(submission.id, submission.taskTitle || task?.title || 'Untitled')}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                        title="Delete this submission permanently"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
        </>
      )}

      {/* Snapshots Tab */}
      {activeTab === 'snapshots' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-100 text-blue-900 text-sm rounded-lg p-4">
            Classification snapshots are immutable datasets from approved submissions. Select a single task in the filters above, then click <span className="font-semibold">Create Snapshot</span> to generate a train/val/test split for that task.
          </div>
          {/* Snapshots Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600">{snapshots.length}</div>
              <div className="text-sm text-gray-600">Total Snapshots</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600">
                {snapshots.reduce((sum, s) => sum + s.counts.train + s.counts.val + s.counts.test, 0)}
              </div>
              <div className="text-sm text-gray-600">Total Images</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">
                {snapshots.reduce((sum, s) => sum + s.counts.positives, 0)}
              </div>
              <div className="text-sm text-gray-600">Positive Samples</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-bold text-orange-600">
                {snapshots.reduce((sum, s) => sum + s.counts.negatives, 0)}
              </div>
              <div className="text-sm text-gray-600">Negative Samples</div>
            </div>
          </div>

          {/* Snapshots Table */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Classification Snapshots</h3>
                  <p className="text-sm text-gray-500">Immutable per-task datasets used for internal model training.</p>
                </div>
              </div>
              {/* Snapshot Filters */}
              <div className="flex flex-wrap gap-3 mt-3">
                <select
                  value={snapshotFilters.taskType}
                  onChange={e => setSnapshotFilters(prev => ({ ...prev, taskType: e.target.value }))}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="Photo">Photo</option>
                  <option value="Video">Video</option>
                </select>
                <select
                  value={snapshotFilters.task}
                  onChange={e => setSnapshotFilters(prev => ({ ...prev, task: e.target.value }))}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Tasks</option>
                  {[...new Set(snapshots.map(s => s.taskId))].map(taskId => (
                    <option key={taskId} value={taskId}>{tasks[taskId]?.title || taskId}</option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={snapshotFilters.hasZeroVal}
                    onChange={e => setSnapshotFilters(prev => ({ ...prev, hasZeroVal: e.target.checked }))}
                    className="rounded"
                  />
                  Show 0 Val only
                </label>
                {(snapshotFilters.task !== 'all' || snapshotFilters.hasZeroVal || snapshotFilters.taskType !== 'all') && (
                  <button
                    onClick={() => setSnapshotFilters({ task: 'all', hasZeroVal: false, taskType: 'all' })}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
            
            {snapshots.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📊</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Classification Snapshots Yet</h3>
                <p className="text-gray-500 mb-4">
                  Create your first snapshot for a task to start training models.
                </p>
                {uniqueTasks.length === 0 ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">No Tasks Available</h4>
                    <p className="text-sm text-red-700 mb-2">
                      You need to have tasks in your database to create snapshots.
                    </p>
                    <p className="text-xs text-red-600">
                      Go to the Tasks page to create some tasks first, then come back here.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => setShowCreateModal(true)}
                      disabled={filters.task === 'all'}
                      className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#00e3ff' }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#00cce6')}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00e3ff'}
                      title={filters.task === 'all' ? 'Select a specific task in the filters to create a training snapshot' : 'Create training snapshot'}
                    >
                      Create Snapshot
                    </button>
                    {filters.task === 'all' && (
                      <p className="text-xs text-gray-500 mt-1">Select a specific task in the filters above.</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Snapshot ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Counts
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Classes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {snapshots
                      .filter(s => snapshotFilters.task === 'all' || s.taskId === snapshotFilters.task)
                      .filter(s => snapshotFilters.taskType === 'all' || (tasks[s.taskId]?.taskType || 'Photo') === snapshotFilters.taskType)
                      .filter(s => !snapshotFilters.hasZeroVal || s.counts.val === 0)
                      .map((snapshot) => (
                      <tr key={snapshot.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {snapshot.name || '—'}
                          </div>
                          {snapshot.counts.val === 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 mt-1">
                              ⚠ 0 val
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {snapshot.id.substring(0, 12)}...
                          </div>
                          <div className="text-sm text-gray-500">
                            {snapshot.taskType}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {tasks[snapshot.taskId]?.title || 'Unknown Task'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            Train: {snapshot.counts.train}, Val: {snapshot.counts.val}, Test: {snapshot.counts.test}
                          </div>
                          <div className="text-sm text-gray-500">
                            Pos: {snapshot.counts.positives}, Neg: {snapshot.counts.negatives}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {snapshot.classes.map((cls, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {cls}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {snapshot.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(snapshot.uri);
                                alert('URI copied to clipboard!');
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Copy URI
                            </button>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(snapshot.id);
                                alert('Snapshot ID copied to clipboard!');
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              Copy ID
                            </button>
                            <button
                              onClick={() => deleteSnapshot(snapshot.id, tasks[snapshot.taskId]?.title || 'Unknown Task')}
                              className="text-red-600 hover:text-red-900"
                              title="Delete this snapshot permanently"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Snapshot from Selection Modal */}
      {showCreateFromSelectionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Dataset Snapshot</h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    You selected <strong>{selectedSubmissionIds.size}</strong> submission{selectedSubmissionIds.size !== 1 ? 's' : ''}.
                  </p>
                  {(() => {
                    const selectedSubmissions = filteredSubmissions.filter(s => selectedSubmissionIds.has(s.id));
                    const taskIds = new Set(selectedSubmissions.map(s => s.taskId));
                    if (taskIds.size === 1) {
                      const taskId = Array.from(taskIds)[0];
                      const task = tasks[taskId];
                      const lastSnapshot = snapshots
                        .filter(s => s.taskId === taskId)
                        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];
                      return (
                        <>
                          <p className="text-sm text-blue-800 mt-1">
                            Task: <strong>{task?.title || taskId}</strong>
                          </p>
                          {lastSnapshot && (
                            <p className="text-xs text-blue-600 mt-1">
                              Last Snapshot: <strong>{lastSnapshot.name || lastSnapshot.id}</strong>
                            </p>
                          )}
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Snapshot Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={snapshotName}
                    onChange={(e) => setSnapshotName(e.target.value)}
                    placeholder="e.g., Handwriting Dataset v1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Split Ratios
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Train</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={snapshotConfig.split.train}
                        onChange={(e) => setSnapshotConfig(prev => ({
                          ...prev,
                          split: { ...prev.split, train: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Val</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={snapshotConfig.split.val}
                        onChange={(e) => setSnapshotConfig(prev => ({
                          ...prev,
                          split: { ...prev.split, val: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Test</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={snapshotConfig.split.test}
                        onChange={(e) => setSnapshotConfig(prev => ({
                          ...prev,
                          split: { ...prev.split, test: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Per Class (optional)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={snapshotConfig.maxPerClass || ''}
                    onChange={(e) => setSnapshotConfig(prev => ({
                      ...prev,
                      maxPerClass: e.target.value ? parseInt(e.target.value) : undefined
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="No limit"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateFromSelectionModal(false);
                    setSnapshotName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createSnapshotFromSelection}
                  disabled={creatingSnapshot || !snapshotName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#00e3ff' }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#00cce6')}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00e3ff'}
                >
                  {creatingSnapshot ? 'Creating...' : 'Create Snapshot'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Snapshot Modal (from Snapshots tab) */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Dataset Snapshot</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task: {tasks[filters.task]?.title || 'Unknown'}
                  </label>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Split Ratios
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Train</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={snapshotConfig.split.train}
                        onChange={(e) => setSnapshotConfig(prev => ({
                          ...prev,
                          split: { ...prev.split, train: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Val</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={snapshotConfig.split.val}
                        onChange={(e) => setSnapshotConfig(prev => ({
                          ...prev,
                          split: { ...prev.split, val: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Test</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={snapshotConfig.split.test}
                        onChange={(e) => setSnapshotConfig(prev => ({
                          ...prev,
                          split: { ...prev.split, test: parseFloat(e.target.value) }
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Per Class (optional)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={snapshotConfig.maxPerClass || ''}
                    onChange={(e) => setSnapshotConfig(prev => ({
                      ...prev,
                      maxPerClass: e.target.value ? parseInt(e.target.value) : undefined
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="No limit"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createSnapshot}
                  disabled={creatingSnapshot}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#00e3ff' }}
                  onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#00cce6')}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00e3ff'}
                >
                  {creatingSnapshot ? 'Creating...' : 'Create Snapshot'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedSubmission && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Submission Details - {selectedSubmission.taskTitle || tasks[selectedSubmission.taskId]?.title || 'Untitled'}
                </h3>
                <div className="flex items-center space-x-2">
                  {!isEditingSubcategory && (
                    <button
                      onClick={handleEditSubcategory}
                      className="px-3 py-1 text-sm bg-[#00e3ff] text-black rounded hover:bg-[#00cce6]"
                    >
                      Edit Subcategory
                    </button>
                  )}
                  {!isEditingItemType && (
                    <button
                      onClick={handleEditItemType}
                      className="px-3 py-1 text-sm bg-[#00e3ff] text-black rounded hover:bg-[#00cce6]"
                    >
                      Edit Item Type
                    </button>
                  )}
                <button
                  onClick={() => {
                      setIsEditingSubcategory(false);
                      setEditingSubcategory('');
                      setIsEditingItemType(false);
                      setEditingItemType('');
                    setShowDetailsModal(false);
                    setSelectedSubmission(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Image with Labels Overlay */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Labeled Image</h4>
                  <div className="relative border border-gray-200 rounded-lg overflow-hidden">
                    <img 
                      ref={(el) => { imageRef.current = el; }}
                      src={selectedSubmission.imageUrl || selectedSubmission.mediaUrl} 
                      alt="Labeled submission" 
                      className="w-full h-auto max-h-96 object-contain"
                    />
                    {/* Labels Overlay (derived) */}
                    {modalLabels.length > 0 && (
                      <div className="absolute inset-0 pointer-events-none">
                        {modalLabels.map((label, index) => (
                          <div
                            key={index}
                            className="absolute border-2 border-red-500 bg-red-500 bg-opacity-20"
                            style={{
                              left: `${label.leftPct}%`,
                              top: `${label.topPct}%`,
                              width: `${label.widthPct}%`,
                              height: `${label.heightPct}%`,
                            }}
                          >
                            <div className="absolute -top-6 left-0 bg-red-500 text-white text-xs px-1 rounded">
                              {label.classId || 'Unknown'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submission Details */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Submission Information</h4>
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Submission ID:</span>
                      <span className="text-gray-900 font-mono text-sm">{selectedSubmission.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Task:</span>
                      <span className="text-gray-900">{tasks[selectedSubmission.taskId]?.title || selectedSubmission.taskTitle || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Category:</span>
                      <span className="text-gray-900">{selectedSubmission.category}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Subcategory:</span>
                      {isEditingSubcategory ? (
                        <div className="flex items-center space-x-2">
                          <select
                            value={editingSubcategory}
                            onChange={(e) => setEditingSubcategory(e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={savingSubcategory}
                          >
                            <option value="">No Subcategory</option>
                            {getAvailableSubcategories().map(sub => (
                              <option key={sub.id} value={sub.name}>{sub.name}</option>
                            ))}
                            {/* Also show current subcategory if it's not in the list (for edge cases) */}
                            {editingSubcategory && !getAvailableSubcategories().some(sub => sub.name === editingSubcategory) && (
                              <option value={editingSubcategory}>{editingSubcategory} (current)</option>
                            )}
                          </select>
                          <button
                            onClick={handleSaveSubcategory}
                            disabled={savingSubcategory}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {savingSubcategory ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={savingSubcategory}
                            className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-900">
                          {tasks[selectedSubmission.taskId]?.subcategory || selectedSubmission.subcategory || 'None'}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Task Type:</span>
                      <span className="text-gray-900">{selectedSubmission.taskType}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Item Type:</span>
                      {isEditingItemType ? (
                        <div className="flex items-center space-x-2">
                          <div>
                            <input
                              type="text"
                              value={editingItemType}
                              onChange={(e) => setEditingItemType(e.target.value)}
                              list="item-type-options"
                              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Enter item type"
                              disabled={savingItemType}
                            />
                            <datalist id="item-type-options">
                              {getAvailableItemTypes().map(type => (
                                <option key={type} value={type} />
                              ))}
                            </datalist>
                          </div>
                          <button
                            onClick={handleSaveItemType}
                            disabled={savingItemType}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {savingItemType ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelItemTypeEdit}
                            disabled={savingItemType}
                            className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-900">
                          {getSubmissionItemType(selectedSubmission) || 'None'}
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {selectedSubmission.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Labels Count:</span>
                      <span className="text-gray-900">{getLabelsCount(selectedSubmission.labels)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">Completed:</span>
                      <span className="text-gray-900">{formatDate(selectedSubmission.completedAt)}</span>
                    </div>
                    {selectedSubmission.labeledBy && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Labeled By:</span>
                        <span className="text-gray-900">{selectedSubmission.labeledBy}</span>
                      </div>
                    )}
                    {selectedSubmission.approvedBy && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Approved By:</span>
                        <span className="text-gray-900">{selectedSubmission.approvedBy}</span>
                      </div>
                    )}
                  </div>

                  {/* Labels Details */}
                  {modalLabels.length > 0 && (
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-900">Labels Details</h5>
                      <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                        {modalLabels.map((label, index) => (
                            <div key={index} className="mb-2 p-2 bg-white rounded border">
                              <div className="text-sm">
                                <span className="font-medium">Class:</span> {label.classId || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-500">
                                Position: ({label.leftPct.toFixed(1)}%, {label.topPct.toFixed(1)}%) 
                                Size: {label.widthPct.toFixed(1)}% × {label.heightPct.toFixed(1)}%
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedSubmission(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
                <button
                  onClick={() => sendBackToQueue(selectedSubmission)}
                  disabled={sendingBackToQueue}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {sendingBackToQueue ? 'Sending...' : 'Send Back to Labeling Queue'}
                </button>
                <button
                  onClick={() => deleteSubmission(selectedSubmission.id, selectedSubmission.taskTitle || tasks[selectedSubmission.taskId]?.title || 'Untitled')}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Permanently Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}




